import { createFileRoute, useNavigate, useBlocker, useLocation } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useRef, useEffect } from 'react';
import api from '../lib/api';
import { Breadcrumb } from '../components/Breadcrumb';
import { StorageUsageDto } from '@prometeus/contracts';
import type { DocumentDto, DocumentHistoryDto, PaginatedDocumentHistoryDto, PaginatedDocumentsDto } from '@prometeus/contracts';
import type { ProcessDto, ClientDto, ConvertCasoToProcessDto } from '@prometeus/contracts';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import { useStorageLimitExceededStore } from '../stores/storageLimitExceeded.store';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { ConvertCasoModal } from '../components/ConvertCasoModal';
import { useEventBusStore } from '../stores/event-bus.store';

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB
const ALLOWED_ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv';
const DEFAULT_FOLDER_COLOR = '#06141B';
const FOLDER_COLOR_PALETTE = [
  '#06141B', '#DC2626', '#EA580C', '#CA8A04', '#65A30D', '#059669', '#0D9488', '#0891B2',
  '#2563EB', '#7C3AED', '#DB2777', '#4B5563', '#78716C',
];

function isAllowedFile(file: File): { ok: true } | { ok: false; message: string } {
  const allowedTypes = [
    'image/',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
  ];
  const mime = file.type || '';
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const typeByMime = allowedTypes.some((t) => t.endsWith('/') ? mime.startsWith(t) : mime === t);
  const typeByExt = ['xls', 'xlsx', 'csv'].includes(ext) && (mime === '' || mime === 'application/octet-stream' || mime === 'application/vnd.ms-excel' || mime.includes('spreadsheet') || mime === 'text/csv');
  const typeOk = typeByMime || typeByExt;
  if (!typeOk) {
    return { ok: false, message: 'Apenas imagens, PDF, DOC/DOCX e planilhas (XLS, XLSX, CSV) são permitidos.' };
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return { ok: false, message: 'O arquivo deve ter no máximo 20 MB.' };
  }
  return { ok: true };
}

function getMimeTypeForUpload(file: File): string {
  const mime = file.type || '';
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'xlsx' && (!mime || mime === 'application/octet-stream' || mime === 'application/zip')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (ext === 'xls' && (!mime || mime === 'application/octet-stream')) return 'application/vnd.ms-excel';
  if (ext === 'csv' && (!mime || mime === 'application/octet-stream')) return 'text/csv';
  return mime || 'application/octet-stream';
}

export const Route = createFileRoute('/w/$workspaceId/clients/$clientId/documents')({
  validateSearch: (search: Record<string, unknown>) => ({
    folder: (search.folder as string) || undefined,
  }),
  component: ClientDocumentsPage,
});

type DocumentWithUrl = DocumentDto & { url: string };

function ClientDocumentsPage() {
  const { workspaceId, clientId } = Route.useParams();
  const { folder: processId } = Route.useSearch();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { requirePermission } = usePermissionGuard();
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);
  const showStorageLimit = useStorageLimitExceededStore((state) => state.show);
  const canRead = hasPermission('documents.read');

  const [searchInput, setSearchInput] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [documentsPage, setDocumentsPage] = useState(1);
  const [foldersPage, setFoldersPage] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showConvertCasoModal, setShowConvertCasoModal] = useState(false);
  const addToast = useEventBusStore((state) => state.addToast);

  const isInsideFolder = !!processId;
  const HISTORY_PAGE_SIZE = 4;
  const DOCUMENTS_PAGE_SIZE = 12;
  const FOLDERS_PAGE_SIZE = 6;

  useEffect(() => {
    if (!location.pathname.startsWith('/w/')) return;
    if (!canRead) {
      showPermissionDenied('Ver documentos');
      navigate({ to: '/w/$workspaceId/clients/$clientId', params: { workspaceId, clientId } });
    }
  }, [location.pathname, canRead, showPermissionDenied, navigate, workspaceId, clientId]);

  useEffect(() => {
    setHistoryPage(1);
  }, [clientId, processId]);

  useEffect(() => {
    setDocumentsPage(1);
  }, [clientId, processId, searchInput]);

  useEffect(() => {
    setFoldersPage(1);
  }, [clientId, searchInput]);

  const { data: workspace } = useQuery<{ name?: string }>({
    queryKey: ['workspaces', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data?.data ?? response.data;
    },
    enabled: !!workspaceId && canRead,
  });

  const { data: client } = useQuery<ClientDto>({
    queryKey: ['clients', workspaceId, clientId],
    queryFn: async () => {
      const response = await api.get(`/clients/${clientId}`);
      return response.data.data;
    },
    enabled: !!clientId && canRead,
  });

  const { data: storageUsage } = useQuery<StorageUsageDto>({
    queryKey: ['storage-usage', workspaceId],
    queryFn: async () => {
      const response = await api.get('/storage/usage', {
        headers: { 'X-Workspace-Id': workspaceId },
      });
      return response.data?.data ?? response.data;
    },
    enabled: !!workspaceId && canRead,
    staleTime: 0,
  });

  const foldersSearchParam = searchInput.trim() || undefined;
  const { data: processesResponse } = useQuery<{ data: ProcessDto[]; total: number; page: number; limit: number; totalPages: number }>({
    queryKey: ['processes', workspaceId, clientId, foldersPage, foldersSearchParam],
    queryFn: async () => {
      const response = await api.get(`/clients/${clientId}/processes`, {
        params: { page: foldersPage, limit: FOLDERS_PAGE_SIZE, includeArchived: true, ...(foldersSearchParam ? { search: foldersSearchParam } : {}) },
      });
      const raw = response.data?.data ?? response.data;
      if (raw?.data !== undefined && typeof raw?.totalPages === 'number') return raw;
      const list = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
      return { data: list, total: list.length, page: 1, limit: FOLDERS_PAGE_SIZE, totalPages: 1 };
    },
    enabled: !!clientId && canRead && !isInsideFolder,
  });

  const processes = processesResponse?.data ?? [];
  const foldersTotalPages = processesResponse?.totalPages ?? 1;

  const { data: currentProcess } = useQuery<ProcessDto>({
    queryKey: ['process', workspaceId, clientId, processId],
    queryFn: async () => {
      const response = await api.get(`/clients/${clientId}/processes/${processId}`);
      return response.data?.data ?? response.data;
    },
    enabled: !!processId && !!clientId && canRead,
  });

  const searchParam = searchInput.trim() || undefined;
  const { data: rootDocumentsResponse, isLoading: loadingRootDocs, isFetching: fetchingRootDocs } = useQuery<PaginatedDocumentsDto>({
    queryKey: ['documents', workspaceId, clientId, 'root', documentsPage, searchParam],
    queryFn: async () => {
      const response = await api.get('/documents', {
        params: { clientId, page: documentsPage, limit: DOCUMENTS_PAGE_SIZE, ...(searchParam ? { search: searchParam } : {}) },
      });
      const raw = response.data?.data ?? response.data;
      if (raw?.data !== undefined) return raw;
      return { data: Array.isArray(raw) ? raw : [], total: 0, page: 1, limit: DOCUMENTS_PAGE_SIZE, totalPages: 1 };
    },
    enabled: !!clientId && canRead && !isInsideFolder,
  });

  const { data: folderDocumentsResponse, isLoading: loadingFolderDocs, isFetching: fetchingFolderDocs } = useQuery<PaginatedDocumentsDto>({
    queryKey: ['documents', workspaceId, processId, documentsPage, searchParam],
    queryFn: async () => {
      const response = await api.get('/documents', {
        params: { processId, page: documentsPage, limit: DOCUMENTS_PAGE_SIZE, ...(searchParam ? { search: searchParam } : {}) },
      });
      const raw = response.data?.data ?? response.data;
      if (raw?.data !== undefined) return raw;
      return { data: Array.isArray(raw) ? raw : [], total: 0, page: 1, limit: DOCUMENTS_PAGE_SIZE, totalPages: 1 };
    },
    enabled: !!processId && canRead,
  });

  const { data: historyResponse } = useQuery<PaginatedDocumentHistoryDto>({
    queryKey: ['documents-history', workspaceId, clientId, processId ?? 'root', historyPage],
    queryFn: async () => {
      const params = { page: historyPage, limit: HISTORY_PAGE_SIZE, ...(isInsideFolder && processId ? { processId } : { clientId }) };
      const response = await api.get('/documents/history', { params });
      return response.data?.data ?? response.data ?? { data: [], total: 0, page: 1, limit: HISTORY_PAGE_SIZE, totalPages: 1 };
    },
    enabled: !!workspaceId && canRead && (!!clientId || !!processId),
  });

  const documentHistory = historyResponse?.data ?? [];
  const historyTotalPages = historyResponse?.totalPages ?? 1;
  const historyTotal = historyResponse?.total ?? 0;

  const documentsResponse = isInsideFolder ? folderDocumentsResponse : rootDocumentsResponse;
  const fetchingDocs = isInsideFolder ? fetchingFolderDocs : fetchingRootDocs;
  // Com termo de pesquisa ativo, não exibir lista antiga enquanto a nova busca carrega
  const showingStaleDuringSearch = !!searchParam && fetchingDocs;
  const documents = showingStaleDuringSearch ? [] : (documentsResponse?.data ?? []);
  const documentsTotal = showingStaleDuringSearch ? 0 : (documentsResponse?.total ?? 0);
  const documentsTotalPages = showingStaleDuringSearch ? 1 : (documentsResponse?.totalPages ?? 1);
  const isLoadingDocs = isInsideFolder ? loadingFolderDocs : loadingRootDocs;
  const showDocsLoading = isLoadingDocs || showingStaleDuringSearch;

  const sortedProcesses = processes;

  const [folderMenuOpenId, setFolderMenuOpenId] = useState<string | null>(null);
  const [folderColorPickerId, setFolderColorPickerId] = useState<string | null>(null);
  const folderMenuRef = useRef<HTMLDivElement>(null);

  const toggleFavoriteMutation = useMutation({
    mutationFn: async (processId: string) => {
      await api.put(`/clients/${clientId}/processes/${processId}/favorite`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processes', workspaceId, clientId] });
      setFolderMenuOpenId(null);
    },
  });

  const updateFolderColorMutation = useMutation({
    mutationFn: async ({ processId, folderColor }: { processId: string; folderColor: string }) => {
      await api.put(`/clients/${clientId}/processes/${processId}`, { folderColor });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processes', workspaceId, clientId] });
      setFolderColorPickerId(null);
      setFolderMenuOpenId(null);
    },
  });

  const convertToProcessMutation = useMutation({
    mutationFn: async (dto: ConvertCasoToProcessDto) => {
      if (!processId) throw new Error('Pasta inválida');
      const response = await api.post(`/clients/${clientId}/processes/${processId}/convert-to-process`, dto);
      return response.data.data;
    },
    onSuccess: () => {
      if (processId) {
        queryClient.invalidateQueries({ queryKey: ['process', workspaceId, clientId, processId] });
      }
      queryClient.invalidateQueries({ queryKey: ['processes', workspaceId, clientId] });
      setShowConvertCasoModal(false);
      addToast('Caso convertido em processo com sucesso', 'success');
    },
    onError: (error: any) => {
      addToast(error.response?.data?.message || 'Erro ao converter caso em processo', 'error');
    },
  });

  const handleDownloadFolderZip = async (processId: string) => {
    setFolderMenuOpenId(null);
    try {
      const response = await api.get(`/documents/process/${processId}/download-zip`, { responseType: 'blob' });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const process = processes.find((p) => p.id === processId);
      const name = (process?.internalTitle || process?.processNumber || 'pasta').replace(/[^a-zA-Z0-9\u00C0-\u024F\s_-]/g, '_').slice(0, 80) || 'pasta';
      link.download = `${name}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Erro ao baixar pasta:', err);
      alert(err.response?.data?.message || 'Erro ao baixar pasta em ZIP.');
    }
  };

  const usedGB = storageUsage
    ? (storageUsage.totalUsed / 1073741824).toFixed(2)
    : '0.00';
  const limitGB = storageUsage
    ? (storageUsage.limit / 1073741824).toFixed(2)
    : '0.00';
  const percentUsed = storageUsage?.percentUsed ?? 0;

  const goToRoot = () => {
    navigate({
      to: '/w/$workspaceId/clients/$clientId/documents',
      params: { workspaceId, clientId },
      search: { folder: undefined },
    });
  };

  const goToFolder = (id: string) => {
    navigate({
      to: '/w/$workspaceId/clients/$clientId/documents',
      params: { workspaceId, clientId },
      search: { folder: id },
    });
  };

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFileName, setUploadingFileName] = useState<string | null>(null);

  const blocker = useBlocker({
    shouldBlockFn: () => isUploading,
    enableBeforeUnload: true,
    withResolver: true,
  });

  const refreshDocs = () => {
    if (isInsideFolder && processId) {
      queryClient.invalidateQueries({ queryKey: ['documents', workspaceId, processId] });
      queryClient.invalidateQueries({ queryKey: ['documents-history', workspaceId, clientId, processId] });
    } else {
      queryClient.invalidateQueries({ queryKey: ['documents', workspaceId, clientId, 'root'] });
      queryClient.invalidateQueries({ queryKey: ['documents-history', workspaceId, clientId, 'root'] });
    }
    queryClient.invalidateQueries({ queryKey: ['storage-usage', workspaceId] });
    queryClient.refetchQueries({ queryKey: ['storage-usage', workspaceId] });
  };

  if (!canRead) return null;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!requirePermission('documents.create', 'Enviar documentos')) return;
    e.target.value = '';
    setUploadError(null);
    const validation = isAllowedFile(file);
    if (!validation.ok) {
      setUploadError(validation.message);
      return;
    }
    setIsUploading(true);
    setUploadProgress(0);
    setUploadingFileName(file.name);
    try {
      const mimeType = getMimeTypeForUpload(file);
      const presignBody = isInsideFolder && processId
        ? { fileName: file.name, mimeType, processId }
        : { fileName: file.name, mimeType, clientId };
      const presignRes = await api.post<{ data?: { url: string; key: string }; url?: string; key?: string }>('/documents/presign', presignBody);
      const payload = presignRes.data;
      const { url, key } = payload?.data ?? payload ?? {};
      if (!url || !key) throw new Error('Resposta do servidor inválida (URL ou key ausente).');
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (ev) => {
          if (ev.lengthComputable) {
            setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload falhou: ${xhr.status}`));
        });
        xhr.addEventListener('error', () => reject(new Error('Erro de rede no upload')));
        xhr.open('PUT', url);
        xhr.setRequestHeader('Content-Type', mimeType);
        xhr.send(file);
      });
      const confirmBody = isInsideFolder && processId
        ? { key, fileName: file.name, mimeType, size: file.size, processId }
        : { key, fileName: file.name, mimeType, size: file.size, clientId };
      await api.post('/documents/confirm', confirmBody);
      refreshDocs();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Erro ao enviar arquivo.';
      if (err.response?.status === 400 && (msg.includes('quota') || msg.includes('Storage'))) {
        showStorageLimit();
      } else if (err.response?.status === 400 && msg.includes('não permitido')) {
        setUploadError(msg);
      } else if (err.response?.status === 400 && msg.includes('20 MB')) {
        setUploadError(msg);
      } else {
        setUploadError(msg);
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadingFileName(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb
          items={
            isInsideFolder && currentProcess
              ? [
                  { label: 'Documentos do workspace', to: '/w/$workspaceId/documents', params: { workspaceId } },
                  { label: client?.name ?? 'Documentos do cliente', to: '/w/$workspaceId/clients/$clientId/documents', params: { workspaceId, clientId } },
                  { label: currentProcess.internalTitle || currentProcess.processNumber || 'Documentos do processo' },
                ]
              : [
                  { label: 'Documentos do workspace', to: '/w/$workspaceId/documents', params: { workspaceId } },
                  { label: client?.name ?? 'Documentos do cliente' },
                ]
          }
        />

        <div className="mt-6 flex gap-6 flex-col lg:flex-row">
          {/* Conteúdo principal */}
          <div className="flex-1 min-w-0 bg-white rounded-[15px] shadow-[0_0_5.5px_rgba(0,0,0,0.02)] p-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="text-[18px] font-semibold truncate" style={{ color: '#2D3748' }}>
                {isInsideFolder && currentProcess
                  ? currentProcess.internalTitle || currentProcess.processNumber || 'Documentos da pasta'
                  : `Documentos${client?.name ? ` de ${client.name}` : ''}`}
              </h1>
              {!isInsideFolder && clientId && (
                <button
                  type="button"
                  onClick={() => navigate({ to: '/w/$workspaceId/clients/$clientId', params: { workspaceId, clientId } })}
                  className="flex-shrink-0 p-1.5 rounded hover:bg-gray-100 transition-colors"
                  title="Ir para a página do cliente"
                  aria-label="Ir para a página do cliente"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </button>
              )}
              {isInsideFolder && currentProcess && processId && clientId && (
                <button
                  type="button"
                  onClick={() => navigate({ to: '/w/$workspaceId/clients/$clientId/processes/$processId', params: { workspaceId, clientId, processId } })}
                  className="flex-shrink-0 p-1.5 rounded hover:bg-gray-100 transition-colors"
                  title="Ir para a página do processo"
                  aria-label="Ir para a página do processo"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {isInsideFolder &&
                currentProcess?.type === 'caso' &&
                !currentProcess.archivedAt &&
                hasPermission('processes.convert') && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!requirePermission('processes.convert', 'Converter caso em processo')) return;
                      setShowConvertCasoModal(true);
                    }}
                    disabled={convertToProcessMutation.isPending}
                    className="px-4 py-2 rounded transition-colors flex items-center gap-2 disabled:opacity-50"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '12px',
                      backgroundColor: '#276749',
                      color: '#F9FAFB',
                      borderRadius: 10,
                    }}
                  >
                    Tornar Processo
                  </button>
                )}
              <button
                type="button"
                onClick={() =>
                  isInsideFolder
                    ? goToRoot()
                    : navigate({ to: '/w/$workspaceId/documents', params: { workspaceId } })
                }
                className="px-4 py-2 rounded transition-colors flex items-center gap-2"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px',
                  backgroundColor: '#F9FAFB',
                  color: '#2D3748',
                }}
                title={isInsideFolder ? 'Voltar para documentos do cliente' : 'Voltar para Documentos do workspace'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Voltar
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept={ALLOWED_ACCEPT}
                onChange={handleFileSelect}
                disabled={isUploading}
              />
              <button
                type="button"
                onClick={() => {
                  if (!requirePermission('documents.create', 'Enviar documentos')) return;
                  if (storageUsage && percentUsed >= 100) { showStorageLimit(); return; }
                  fileInputRef.current?.click();
                }}
                disabled={isUploading}
                    className="px-4 py-2 rounded transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#FFFFFF',
                      backgroundColor: '#485A6A',
                      border: 'none',
                      borderRadius: '8px',
                    }}
                  >
                    {isUploading ? 'Enviando...' : 'Enviar'}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  </button>
            </div>
          </div>
          {uploadError && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {uploadError}
            </div>
          )}
          {isUploading && uploadingFileName && (
            <div className="mb-4 p-4 rounded-lg border border-gray-200 bg-gray-50" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <p className="text-[12px] font-medium text-gray-700 mb-2 truncate" title={uploadingFileName}>
                Enviando: {uploadingFileName}
              </p>
              <div
                className="h-2 rounded-full overflow-hidden transition-all duration-300"
                style={{ backgroundColor: '#E5E7EB' }}
              >
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%`, backgroundColor: '#485A6A' }}
                />
              </div>
              <p className="text-[11px] text-gray-500 mt-1">{uploadProgress}%</p>
            </div>
          )}
          {blocker.status === 'blocked' && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-labelledby="upload-blocker-title">
              <div className="bg-white rounded-[15px] shadow-xl p-6 max-w-sm mx-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <h2 id="upload-blocker-title" className="text-[16px] font-semibold text-gray-900 mb-2">
                  Upload em andamento
                </h2>
                <p className="text-[14px] text-gray-600 mb-4">
                  O upload será interrompido se você sair desta tela. Deseja sair mesmo assim?
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => blocker.reset?.()}
                    className="px-4 py-2 rounded transition-colors"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#2D3748',
                      backgroundColor: '#F9FAFB',
                      border: '1px solid #D9D9D9',
                      borderRadius: '8px',
                    }}
                  >
                    Ficar
                  </button>
                  <button
                    type="button"
                    onClick={() => blocker.proceed?.()}
                    className="px-4 py-2 rounded transition-colors"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#FFFFFF',
                      backgroundColor: '#485A6A',
                      border: 'none',
                      borderRadius: '8px',
                    }}
                  >
                    Sair
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Barra de progresso armazenamento */}
          {storageUsage && (
            <div className="mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <p className="text-[12px] text-gray-600 mb-1">
                {usedGB} GB usado de {limitGB} GB
              </p>
              <div
                className="h-2 rounded-full overflow-hidden"
                style={{ backgroundColor: '#E5E7EB' }}
              >
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(percentUsed, 100)}%`,
                    backgroundColor: percentUsed >= 100 ? '#DC2626' : percentUsed >= 85 ? '#F59E0B' : '#485A6A',
                  }}
                />
              </div>
            </div>
          )}

          {/* Barra de pesquisa */}
          <div className="relative w-full max-w-md mb-6">
            <input
              type="text"
              placeholder="Pesquisar nos documentos"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-[10px] border border-gray-200 text-gray-900 placeholder-gray-500"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '12px',
                backgroundColor: '#F9FAFB',
                outline: 'none',
                boxShadow: 'none',
              }}
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {isInsideFolder ? (
            <>
              <div className="flex justify-between items-center mb-3 gap-2">
                <h2 className="text-[14px] font-semibold" style={{ color: '#2D3748' }}>
                  Documentos
                </h2>
                {documentsTotalPages > 1 && (
                  <div className="flex items-center gap-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <button
                      type="button"
                      onClick={() => setDocumentsPage((p) => Math.max(1, p - 1))}
                      disabled={documentsPage <= 1}
                      className="w-8 h-8 flex items-center justify-center rounded-[8px] transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ fontSize: '14px', fontWeight: 500, color: '#2D3748', backgroundColor: '#F9FAFB', border: '1px solid #D9D9D9' }}
                      aria-label="Página anterior"
                    >
                      &lt;
                    </button>
                    <span className="px-3 text-[12px] font-medium" style={{ color: '#2D3748' }}>
                      {documentsPage} de {documentsTotalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setDocumentsPage((p) => Math.min(documentsTotalPages, p + 1))}
                      disabled={documentsPage >= documentsTotalPages}
                      className="w-8 h-8 flex items-center justify-center rounded-[8px] transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ fontSize: '14px', fontWeight: 500, color: '#2D3748', backgroundColor: '#F9FAFB', border: '1px solid #D9D9D9' }}
                      aria-label="Próxima página"
                    >
                      &gt;
                    </button>
                  </div>
                )}
              </div>
              <DocumentsList
                documents={documents}
                isLoading={showDocsLoading}
                onRefresh={refreshDocs}
                onDocumentDeleted={() => { queryClient.invalidateQueries({ queryKey: ['clients', workspaceId, clientId] }); queryClient.refetchQueries({ queryKey: ['storage-usage', workspaceId] }); queryClient.invalidateQueries({ queryKey: ['documents-history', workspaceId, clientId, processId ?? 'root'] }); queryClient.invalidateQueries({ queryKey: ['documents', workspaceId, processId] }); }}
                processId={processId}
                workspaceId={workspaceId}
                clientId={clientId}
              />
            </>
          ) : (
            <>
              {/* Pastas (processos) - favoritos primeiro */}
              <section className="mb-8">
                <div className="flex justify-between items-center mb-3 gap-2">
                  <h2 className="text-[14px] font-semibold" style={{ color: '#2D3748' }}>
                    Pastas
                  </h2>
                  {foldersTotalPages > 1 && (
                    <div className="flex items-center gap-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      <button
                        type="button"
                        onClick={() => setFoldersPage((p) => Math.max(1, p - 1))}
                        disabled={foldersPage <= 1}
                        className="w-8 h-8 flex items-center justify-center rounded-[8px] transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontSize: '14px', fontWeight: 500, color: '#2D3748', backgroundColor: '#F9FAFB', border: '1px solid #D9D9D9' }}
                        aria-label="Página anterior"
                      >
                        &lt;
                      </button>
                      <span className="px-3 text-[12px] font-medium" style={{ color: '#2D3748' }}>
                        {foldersPage} de {foldersTotalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setFoldersPage((p) => Math.min(foldersTotalPages, p + 1))}
                        disabled={foldersPage >= foldersTotalPages}
                        className="w-8 h-8 flex items-center justify-center rounded-[8px] transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontSize: '14px', fontWeight: 500, color: '#2D3748', backgroundColor: '#F9FAFB', border: '1px solid #D9D9D9' }}
                        aria-label="Próxima página"
                      >
                        &gt;
                      </button>
                    </div>
                  )}
                </div>
                <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                  {sortedProcesses.map((process) => {
                    const folderColor = process.folderColor || DEFAULT_FOLDER_COLOR;
                    const isMenuOpen = folderMenuOpenId === process.id;
                    return (
                      <div
                        key={process.id}
                        className="relative flex items-center gap-3 p-3 rounded-[10px] border border-gray-200 hover:bg-gray-50 text-left transition-colors group min-w-0"
                      >
                        <button
                          type="button"
                          onClick={() => goToFolder(process.id)}
                          className="flex items-center gap-3 min-w-0 flex-1 text-left overflow-hidden"
                        >
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: folderColor }}
                          >
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z" />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1 overflow-hidden">
                            <p className="text-[11px] font-medium truncate block w-full" style={{ color: '#2D3748' }} title={process.processNumber || ''}>
                              {process.processNumber || 'Sem número'}
                            </p>
                            <p className="text-[10px] text-gray-500 truncate block w-full mt-0.5" title={process.internalTitle ? `${process.internalTitle}${client?.name ? ` · em ${client.name}` : ''}` : (client?.name ? `em ${client.name}` : '')}>
                              {process.internalTitle ? `${process.internalTitle}${client?.name ? ` · em ${client.name}` : ''}` : (client?.name ? `em ${client.name}` : '')}
                            </p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFolderMenuOpenId(isMenuOpen ? null : process.id);
                            setFolderColorPickerId(null);
                          }}
                          className="p-1.5 rounded hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          aria-label="Opções da pasta"
                        >
                          <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 20q-.825 0-1.412-.587T10 18t.588-1.412T12 16t1.413.588T14 18t-.587 1.413T12 20m0-6q-.825 0-1.412-.587T10 12t.588-1.412T12 10t1.413.588T14 12t-.587 1.413T12 14m0-6q-.825 0-1.412-.587T10 6t.588-1.412T12 4t1.413.588T14 6t-.587 1.413T12 8" />
                          </svg>
                        </button>
                        {isMenuOpen && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              aria-hidden="true"
                              onClick={() => {
                                setFolderMenuOpenId(null);
                                setFolderColorPickerId(null);
                              }}
                            />
                            <div
                              ref={folderMenuRef}
                              className="absolute right-0 top-full mt-1 z-50 min-w-[200px] bg-white rounded-lg shadow-lg border border-gray-200 py-1"
                              style={{ fontFamily: 'Poppins, sans-serif' }}
                            >
                              <button
                                type="button"
                                onClick={() => handleDownloadFolderZip(process.id)}
                                className="w-full text-left px-4 py-2 text-[13px] text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Baixar
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleFavoriteMutation.mutate(process.id)}
                                disabled={toggleFavoriteMutation.isPending}
                                className="w-full text-left px-4 py-2 text-[13px] text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <svg className="w-4 h-4 text-gray-500" fill={process.isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                </svg>
                                {process.isFavorite ? 'Remover de Com estrela' : 'Adicionar a "Com estrela"'}
                              </button>
                              <div className="border-t border-gray-100 my-1" />
                              <div className="px-4 py-2">
                                <p className="text-[11px] font-medium text-gray-500 mb-2">Cor da pasta</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {FOLDER_COLOR_PALETTE.map((color) => (
                                    <button
                                      key={color}
                                      type="button"
                                      onClick={() => updateFolderColorMutation.mutate({ processId: process.id, folderColor: color })}
                                      disabled={updateFolderColorMutation.isPending}
                                      className="w-6 h-6 rounded-full border-2 border-white shadow"
                                      style={{
                                        backgroundColor: color,
                                        borderColor: (process.folderColor || DEFAULT_FOLDER_COLOR) === color ? '#6366F1' : 'transparent',
                                        boxShadow: (process.folderColor || DEFAULT_FOLDER_COLOR) === color ? '0 0 0 2px #6366F1' : undefined,
                                      }}
                                      title={color}
                                    >
                                      {(process.folderColor || DEFAULT_FOLDER_COLOR) === color && (
                                        <svg className="w-4 h-4 mx-auto text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Documentos do cliente (raiz) */}
              <section>
                <div className="flex justify-between items-center mb-3 gap-2">
                  <h2 className="text-[14px] font-semibold" style={{ color: '#2D3748' }}>
                    Documentos do cliente
                  </h2>
                  {documentsTotalPages > 1 && (
                    <div className="flex items-center gap-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      <button
                        type="button"
                        onClick={() => setDocumentsPage((p) => Math.max(1, p - 1))}
                        disabled={documentsPage <= 1}
                        className="w-8 h-8 flex items-center justify-center rounded-[8px] transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontSize: '14px', fontWeight: 500, color: '#2D3748', backgroundColor: '#F9FAFB', border: '1px solid #D9D9D9' }}
                        aria-label="Página anterior"
                      >
                        &lt;
                      </button>
                      <span className="px-3 text-[12px] font-medium" style={{ color: '#2D3748' }}>
                        {documentsPage} de {documentsTotalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setDocumentsPage((p) => Math.min(documentsTotalPages, p + 1))}
                        disabled={documentsPage >= documentsTotalPages}
                        className="w-8 h-8 flex items-center justify-center rounded-[8px] transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontSize: '14px', fontWeight: 500, color: '#2D3748', backgroundColor: '#F9FAFB', border: '1px solid #D9D9D9' }}
                        aria-label="Próxima página"
                      >
                        &gt;
                      </button>
                    </div>
                  )}
                </div>
                <DocumentsList documents={documents} isLoading={showDocsLoading} onRefresh={refreshDocs} onDocumentDeleted={() => { queryClient.invalidateQueries({ queryKey: ['clients', workspaceId, clientId] }); queryClient.refetchQueries({ queryKey: ['storage-usage', workspaceId] }); queryClient.invalidateQueries({ queryKey: ['documents-history', workspaceId, clientId, processId ?? 'root'] }); queryClient.invalidateQueries({ queryKey: ['documents', workspaceId, clientId] }); }} />
              </section>
            </>
          )}
          <p className="mt-4 pt-4 text-right text-[11px] text-gray-500" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Imagens, PDF, DOC/DOCX ou planilhas (XLS, XLSX, CSV), até 20 MB
          </p>
          </div>

          {/* Painel direito: Histórico de alterações */}
          <aside className="w-full lg:w-80 flex-shrink-0 bg-white rounded-[15px] shadow-[0_0_5.5px_rgba(0,0,0,0.02)] p-4 h-fit lg:sticky lg:top-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            <h2 className="text-[14px] font-semibold mb-3" style={{ color: '#2D3748' }}>
              Histórico de alterações
            </h2>
            <div className="space-y-2 max-h-[calc(100vh-14rem)] overflow-y-auto">
              {documentHistory.length === 0 ? (
                <p className="text-[12px]" style={{ color: '#6B7280' }}>Nenhuma alteração registrada.</p>
              ) : (
                documentHistory.map((entry) => (
                  <div
                    key={entry.id}
                    className="p-3 rounded-lg text-left border transition-colors"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      backgroundColor: '#F9FAFB',
                      borderColor: '#E5E7EB',
                    }}
                  >
                    <p className="text-[12px] font-medium truncate" style={{ color: '#2D3748' }} title={entry.fileName}>
                      {entry.fileName}
                    </p>
                    <p className="text-[12px] mt-1" style={{ color: '#6B7280' }}>
                      {entry.action === 'created' ? 'Enviado' : 'Excluído'} por <span className="font-medium" style={{ color: '#2D3748' }}>{entry.userEmail}</span>
                    </p>
                    <p className="text-[11px] mt-0.5" style={{ color: '#9CA3AF' }}>
                      {new Date(entry.createdAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                    </p>
                  </div>
                ))
              )}
            </div>
            {historyTotal > 0 && (
              <div className="mt-3 pt-3 flex items-center justify-between gap-2" style={{ borderTop: '1px solid #E5E7EB' }}>
                <p className="text-[12px]" style={{ color: '#6B7280', fontFamily: 'Poppins, sans-serif' }}>
                  Página {historyResponse?.page ?? 1} de {historyTotalPages}
                  <span className="ml-1">({historyTotal} {historyTotal === 1 ? 'registro' : 'registros'})</span>
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                    disabled={historyPage <= 1}
                    className="px-3 py-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#2D3748',
                      backgroundColor: '#F9FAFB',
                      border: '1px solid #D9D9D9',
                      borderRadius: '8px',
                    }}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
                    disabled={historyPage >= historyTotalPages}
                    className="px-3 py-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#2D3748',
                      backgroundColor: '#F9FAFB',
                      border: '1px solid #D9D9D9',
                      borderRadius: '8px',
                    }}
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>

      {showConvertCasoModal && (
        <ConvertCasoModal
          onClose={() => setShowConvertCasoModal(false)}
          onConfirm={(dto) => convertToProcessMutation.mutate(dto)}
          isSaving={convertToProcessMutation.isPending}
        />
      )}
    </div>
  );
}

function DocumentsList({
  documents,
  isLoading,
  onRefresh,
  onDocumentDeleted,
  processId,
  workspaceId,
  clientId,
}: {
  documents: DocumentWithUrl[];
  isLoading: boolean;
  onRefresh: () => void;
  onDocumentDeleted?: () => void;
  processId?: string;
  workspaceId?: string;
  clientId?: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { requirePermission } = usePermissionGuard();
  const { confirm } = useConfirmDialog();
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/documents/${id}`);
    },
    onSuccess: () => {
      onRefresh();
      onDocumentDeleted?.();
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['storage-usage'] });
    },
    onError: (err: any) => {
      alert(err.response?.data?.message || 'Erro ao excluir documento.');
    },
  });

  if (isLoading) {
    return <p className="text-sm text-gray-500">Carregando documentos...</p>;
  }
  if (documents.length === 0) {
    return (
      <div className="w-full py-8 flex items-center justify-center">
        <p className="text-[12px] text-gray-500 text-center">Nenhum documento aqui.</p>
      </div>
    );
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getDocumentIcon = (fileName: string, mimeType: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'pdf' || mimeType === 'application/pdf') return 'pdf';
    if (['xls', 'xlsx', 'csv'].includes(ext) || mimeType.includes('spreadsheet') || mimeType === 'text/csv' || mimeType.includes('ms-excel')) return 'planilha';
    if (['doc', 'docx'].includes(ext) || mimeType.includes('wordprocessing')) return 'doc';
    if (mimeType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'tif', 'tiff'].includes(ext)) return 'image';
    return 'doc';
  };

  return (
    <div className="grid gap-3 min-w-0" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
      {documents.map((doc) => {
        const iconName = getDocumentIcon(doc.fileName ?? '', doc.mimeType ?? '');
        return (
        <div
          key={doc.id}
          className="relative flex flex-col p-3 rounded-[10px] border border-gray-200 hover:bg-gray-50 group min-w-0"
        >
          <div className="flex items-start gap-2 min-w-0">
            <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center" title={doc.fileName ?? ''}>
              <img src={`/document-types/${iconName}.svg`} alt="" className="w-6 h-6 object-contain" />
            </div>
            <a
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 min-w-0 block overflow-hidden pr-11"
            >
              <p className="text-[12px] font-medium truncate w-full" style={{ color: '#2D3748' }} title={doc.fileName}>
                {doc.fileName}
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5 whitespace-nowrap truncate">
                {formatSize(doc.size)} · {new Date(doc.createdAt).toLocaleDateString('pt-BR')}
              </p>
            </a>
          </div>
          <div className="absolute right-2 top-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded p-0.5">
            <a
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded hover:bg-gray-200"
              title="Download"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </a>
            {processId && workspaceId && clientId && doc.taskId && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  navigate({
                    to: '/w/$workspaceId/clients/$clientId/processes/$processId/tasks',
                    params: { workspaceId, clientId, processId },
                    search: { taskId: doc.taskId },
                  });
                }}
                className="p-1.5 rounded hover:bg-gray-200"
                title="Ir para a tarefa"
              >
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={async (e) => {
                e.preventDefault();
                if (!requirePermission('documents.delete', 'Excluir documentos')) return;
                const ok = await confirm({
                  title: 'Excluir documento?',
                  description: doc.fileName
                    ? `O arquivo "${doc.fileName}" será excluído permanentemente. Esta ação não pode ser desfeita.`
                    : 'Este documento será excluído permanentemente. Esta ação não pode ser desfeita.',
                  confirmLabel: 'Excluir',
                  variant: 'danger',
                });
                if (ok) deleteMutation.mutate(doc.id);
              }}
              disabled={deleteMutation.isPending}
              className="p-1.5 rounded hover:bg-red-100 text-red-600"
              title="Excluir"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      );
      })}
    </div>
  );
}
